import {
  FC,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useRecoilValue } from "recoil";

import {
  DEFAULT_TILES_SERVICE,
  googleSession,
  mapboxAccessToken,
  systemSettingsDtoAtom,
  TilesServiceEnum,
} from "@ryde/shared";

import {
  LAYER_OPTIONS,
  MIN_SELECTABLE_LAYERS,
} from "./maplibre-layer-control.constants";
import {
  MapLibreLayerControlStyled,
  MapLibreLayerOptionsStyled,
  MapLibreLayerOptionStyled,
  MapLibreLayerToggleStyled,
} from "./maplibre-layer-control.styled";
import { MapLibreLayersIcon } from "./maplibre-layers-icon";

interface IMapLibreLayerControlProps {
  selectedTilesService: TilesServiceEnum | null;
  onSelectTilesService: (service: TilesServiceEnum) => void;
}

/**
 * Client-side base-tile selector (OpenStreet / Google / Mapbox) — the MapLibre
 * twin of the Leaflet `MapLayerBase` `LayersControl`. Selecting a layer rebuilds
 * the raster style via `useMapLibreRasterStyle`; it does not change the company
 * default. Google/Mapbox appear only when their session/token is configured.
 */
export const MapLibreLayerControl: FC<IMapLibreLayerControlProps> = ({
  selectedTilesService,
  onSelectTilesService,
}) => {
  const systemSettings = useRecoilValue(systemSettingsDtoAtom);
  const sessionObj = useRecoilValue(googleSession);
  /*
   * One radio group per control, not one per app. Two maps can be on screen at once — a grid map behind a
   * drawer, say — and a shared group name lets the browser uncheck the other map's selection when this one
   * changes, leaving its control disagreeing with the tiles it is actually showing.
   */
  const radioGroupName = useId();
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const wasOpenRef = useRef(false);

  // Keyboard operability: opening moves focus into the option list (first radio) so
  // keyboard users land inside the control; closing returns focus to the toggle.
  useEffect(() => {
    if (isOpen) {
      optionsRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    } else if (wasOpenRef.current) {
      toggleRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const effectiveService =
    selectedTilesService ??
    systemSettings?.metadata?.tilesService ??
    DEFAULT_TILES_SERVICE;

  const availableOptions = useMemo(
    () =>
      LAYER_OPTIONS.filter(({ service }) => {
        if (service === TilesServiceEnum.Google) {
          return Boolean(sessionObj?.session);
        }
        if (service === TilesServiceEnum.Mapbox) {
          return Boolean(mapboxAccessToken());
        }
        return true;
      }),
    [sessionObj?.session],
  );

  if (availableOptions.length < MIN_SELECTABLE_LAYERS) {
    return null;
  }

  const handleSelect = (service: TilesServiceEnum): void => {
    onSelectTilesService(service);
    setIsOpen(false);
  };

  // Close the base-tile selector on Escape (keyboard dismissal).
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <MapLibreLayerControlStyled
      onMouseLeave={() => setIsOpen(false)}
      onKeyDown={handleKeyDown}
    >
      {isOpen ? (
        <MapLibreLayerOptionsStyled ref={optionsRef}>
          {availableOptions.map(({ service, label }) => (
            <li key={service}>
              <MapLibreLayerOptionStyled>
                <input
                  type="radio"
                  name={radioGroupName}
                  checked={effectiveService === service}
                  onChange={() => handleSelect(service)}
                />
                {label}
              </MapLibreLayerOptionStyled>
            </li>
          ))}
        </MapLibreLayerOptionsStyled>
      ) : (
        <MapLibreLayerToggleStyled
          ref={toggleRef}
          type="button"
          aria-label="Map layers"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(true)}
        >
          <MapLibreLayersIcon />
        </MapLibreLayerToggleStyled>
      )}
    </MapLibreLayerControlStyled>
  );
};
